/**
 * @page /portal/schulungen/:zuweisungId (MA-Portal: Schulungs-Tour + Test-Runner)
 * @description 4-Phasen-Flow: Tour → Test → Submit → Result
 * @api GET/POST /api/portal/schulungen/:zuweisungId, /api/portal/schulungen/versuch/:versuchId/submit
 * @roles portal_mitarbeiter
 * @i18n qmTest.*
 */
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, ArrowRight, Award, CheckCircle2, AlertTriangle, Clock, Loader2, Trophy, XCircle } from 'lucide-react';
import ProcessHtmlView from '../components/ProcessHtmlView';

const API = import.meta.env.VITE_API_URL || '';

export default function PortalSchulungDetail() {
  const { t } = useLanguage();
  const { zuweisungId } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('intro');           // intro | tour | test | result
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourIdx, setTourIdx] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Test-Runner state
  const [versuch, setVersuch] = useState(null);          // { versuch_id, fragen, bestehensgrenze_pct }
  const [answers, setAnswers] = useState({});            // { frageId: [antwortIds] }
  const [frageIdx, setFrageIdx] = useState(0);

  // Result state
  const [result, setResult] = useState(null);

  // #1735 — Bei Phasen-/Schritt-/Fragenwechsel nach oben scrollen (Mobile: oberer Teil blieb sonst unsichtbar)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [phase, tourIdx, frageIdx]);

  /* ── Load assignment ── */
  useEffect(() => {
    fetch(`${API}/api/portal/schulungen/${zuweisungId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => {
        setData(d);
        setLoading(false);
        if (d.status === 'bestanden') {
          setResult({
            bestanden: true,
            score_pct: d.versuche?.[0]?.score_pct || 100,
            bestehensgrenze_pct: d.bestehensgrenze_pct,
            isFromHistory: true,
          });
          setPhase('result');
        } else {
          // confirmed steps
          const arr = Array.isArray(d.tour_bestaetigte_schritte) ? d.tour_bestaetigte_schritte : [];
          setTourIdx(arr.length); // resume at first un-confirmed step
        }
      })
      .catch(() => { setError('not_found'); setLoading(false); });
  }, [zuweisungId]);

  /* ── Tour helpers ── */
  const schritte = data?.schritte || [];
  const bestaetigtArr = useMemo(() => Array.isArray(data?.tour_bestaetigte_schritte) ? data.tour_bestaetigte_schritte : [], [data]);
  const tourComplete = schritte.length > 0 && schritte.every(s => bestaetigtArr.includes(s.id));

  const confirmStep = async () => {
    const s = schritte[tourIdx];
    if (!s) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API}/api/portal/schulungen/${zuweisungId}/tour-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ schritt_id: s.id }),
      });
      if (!res.ok) throw new Error();
      const r = await res.json();
      setData(prev => ({ ...prev, tour_bestaetigte_schritte: r.bestaetigt }));
      if (tourIdx < schritte.length - 1) {
        setTourIdx(tourIdx + 1);
      }
    } finally {
      setConfirming(false);
    }
  };

  const startTest = async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/portal/schulungen/${zuweisungId}/start`, {
        method: 'POST',
        credentials: 'include',
      });
      const r = await res.json();
      if (!res.ok) {
        setError(r.error || 'Test konnte nicht gestartet werden');
        setStarting(false);
        return;
      }
      setVersuch(r);
      setAnswers({});
      setFrageIdx(0);
      setPhase('test');
    } catch {
      setError('Netzwerkfehler');
    } finally {
      setStarting(false);
    }
  };

  /* ── Test-Runner helpers ── */
  const toggleAnswer = (frageId, antwortId, isMulti) => {
    setAnswers(prev => {
      const current = prev[frageId] || [];
      if (isMulti) {
        return {
          ...prev,
          [frageId]: current.includes(antwortId) ? current.filter(x => x !== antwortId) : [...current, antwortId],
        };
      }
      return { ...prev, [frageId]: [antwortId] };
    });
  };

  const submitTest = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/portal/schulungen/versuch/${versuch.versuch_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ antworten: answers }),
      });
      const r = await res.json();
      if (!res.ok) {
        setError(r.error || 'Submit fehlgeschlagen');
        setSubmitting(false);
        return;
      }
      setResult(r);
      setPhase('result');
    } catch {
      setError('Netzwerkfehler');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Render ── */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-app, #F8F9FC)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#338edc' }} />
      </div>
    );
  }
  if (error === 'not_found' || !data) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center" style={{ background: 'var(--bg-app, #F8F9FC)' }}>
        <AlertTriangle className="w-10 h-10 mb-3" style={{ color: '#F59E0B' }} />
        <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          {t('qmTest.notFound') || 'Schulung nicht gefunden'}
        </p>
        <button onClick={() => navigate('/portal/schulungen')} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: '#338edc', color: '#fff' }}>
          {t('qmTest.back') || 'Zurück'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-app, #F8F9FC)' }}>
      {/* Header */}
      <div className="px-4 py-4 flex items-center gap-3 border-b sticky top-0 z-10" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #FFFFFF)' }}>
        <button
          onClick={() => navigate('/portal/schulungen')}
          className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} style={{ color: 'var(--text-primary, #1A1A2E)' }} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs truncate" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{data.prozess_titel}</p>
          <h1 className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {data.test_titel}
          </h1>
        </div>
      </div>

      <div className="p-6 space-y-4 pb-24">

        {/* === INTRO === */}
        {phase === 'intro' && (
          <div className="space-y-4">
            <div className="rounded-2xl p-5" style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                  <Award className="w-6 h-6" strokeWidth={1.5} style={{ color: '#338edc' }} />
                </div>
                <div>
                  <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                    {data.test_titel}
                  </h2>
                  <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{data.prozess_titel}</p>
                </div>
              </div>
              {data.test_beschreibung && (
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {data.test_beschreibung}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg p-3" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
                  <p style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('qmTest.bestehensgrenze') || 'Bestehensgrenze'}</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{data.bestehensgrenze_pct}%</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
                  <p style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('qmTest.fragenAnzahl') || 'Fragen'}</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                    {data.anzahl_fragen_aus_pool || (t('qmTest.fragenAlle') || 'alle')}
                  </p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
                  <p style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('qmTest.maxVersuche') || 'Max. Versuche'}</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{data.max_versuche}</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
                  <p style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('qmTest.zeitlimit') || 'Zeitlimit'}</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                    {data.zeitlimit_minuten ? `${data.zeitlimit_minuten} min` : (t('qmTest.keinLimit') || 'kein Limit')}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-4" style={{ background: '#EFF6FF', border: '1px solid #338edc30' }}>
              <p className="text-sm font-medium mb-1" style={{ color: '#1e40af' }}>
                {t('qmTest.tourHinweisTitle') || 'Schulungs-Tour vor dem Test'}
              </p>
              <p className="text-xs" style={{ color: '#1e40af' }}>
                {t('qmTest.tourHinweis') || 'Du gehst zuerst alle Schritte des Prozesses durch. Erst danach kannst du den Test starten.'}
              </p>
            </div>

            <button
              onClick={() => setPhase('tour')}
              className="w-full py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2"
              style={{ background: '#338edc', color: '#fff' }}
            >
              {tourComplete
                ? (t('qmTest.tourErneut') || 'Tour ansehen')
                : (t('qmTest.tourStarten') || 'Tour starten')}
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
            {tourComplete && (
              <button
                onClick={startTest}
                disabled={starting}
                className="w-full py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#10B981', color: '#fff' }}
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                {t('qmTest.testStarten') || 'Test starten'}
              </button>
            )}
            {error && (
              <div className="rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* === TOUR === */}
        {phase === 'tour' && schritte.length > 0 && (() => {
          const step = schritte[tourIdx] || schritte[schritte.length - 1];
          const stepConfirmed = bestaetigtArr.includes(step.id);
          const isLast = tourIdx >= schritte.length - 1;
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                  {t('qmTest.schritt') || 'Schritt'} {tourIdx + 1} / {schritte.length}
                </p>
                <div className="flex gap-1">
                  {schritte.map((_, i) => (
                    <div key={i} className="h-1 w-6 rounded-full" style={{ background: i <= tourIdx ? '#338edc' : 'var(--border-card, #E5E7EB)' }} />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl p-5" style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
                <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {step.titel}
                </h3>
                {step.beschreibung && (
                  <ProcessHtmlView html={step.beschreibung} className="text-sm" />
                )}
              </div>

              <div className="flex gap-2">
                {tourIdx > 0 && (
                  <button
                    onClick={() => setTourIdx(tourIdx - 1)}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium border"
                    style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)', background: 'var(--bg-card, #FFFFFF)' }}
                  >
                    {t('qmTest.zurueck') || 'Zurück'}
                  </button>
                )}
                {!stepConfirmed ? (
                  <button
                    onClick={confirmStep}
                    disabled={confirming}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#10B981', color: '#fff' }}
                  >
                    {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {t('qmTest.verstanden') || 'Verstanden'}
                  </button>
                ) : !isLast ? (
                  <button
                    onClick={() => setTourIdx(tourIdx + 1)}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2"
                    style={{ background: '#338edc', color: '#fff' }}
                  >
                    {t('qmTest.weiter') || 'Weiter'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={startTest}
                    disabled={starting || !tourComplete}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#338edc', color: '#fff' }}
                  >
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                    {t('qmTest.testStarten') || 'Test starten'}
                  </button>
                )}
              </div>
              {error && (
                <div className="rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>
                  {error}
                </div>
              )}
            </div>
          );
        })()}

        {/* === TEST === */}
        {phase === 'test' && versuch && (() => {
          const frage = versuch.fragen[frageIdx];
          if (!frage) return null;
          const isMulti = frage.frage_typ === 'multi_choice';
          const userAnswers = answers[frage.id] || [];
          const isLast = frageIdx >= versuch.fragen.length - 1;
          const beantwortet = versuch.fragen.filter(f => (answers[f.id] || []).length > 0).length;
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                  {t('qmTest.frage') || 'Frage'} {frageIdx + 1} / {versuch.fragen.length}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                  {beantwortet}/{versuch.fragen.length} {t('qmTest.beantwortet') || 'beantwortet'}
                </p>
              </div>
              <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: 'var(--border-card, #E5E7EB)' }}>
                <div className="h-full" style={{ width: `${((frageIdx + 1) / versuch.fragen.length) * 100}%`, background: '#338edc' }} />
              </div>

              <div className="rounded-2xl p-5" style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
                <p className="text-xs mb-2" style={{ color: '#338edc' }}>
                  {isMulti ? (t('qmTest.multiHint') || 'Mehrere Antworten möglich') : (t('qmTest.singleHint') || 'Eine Antwort wählen')}
                </p>
                <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {frage.frage_text}
                </h3>
                <div className="space-y-2">
                  {frage.antworten.map(a => {
                    const sel = userAnswers.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        onClick={() => toggleAnswer(frage.id, a.id, isMulti)}
                        className="w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3"
                        style={{
                          borderColor: sel ? '#338edc' : 'var(--border-card, #E5E7EB)',
                          background: sel ? '#EFF6FF' : 'var(--bg-card, #FFFFFF)',
                        }}
                      >
                        <div
                          className="w-5 h-5 shrink-0 flex items-center justify-center"
                          style={{
                            borderRadius: isMulti ? '4px' : '999px',
                            border: `2px solid ${sel ? '#338edc' : 'var(--border-card, #D1D5DB)'}`,
                            background: sel ? '#338edc' : 'transparent',
                          }}
                        >
                          {sel && <CheckCircle2 className="w-3 h-3" style={{ color: '#fff' }} strokeWidth={3} />}
                        </div>
                        <span className="text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {a.antwort_text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2">
                {frageIdx > 0 && (
                  <button
                    onClick={() => setFrageIdx(frageIdx - 1)}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium border"
                    style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)', background: 'var(--bg-card, #FFFFFF)' }}
                  >
                    {t('qmTest.zurueck') || 'Zurück'}
                  </button>
                )}
                {!isLast ? (
                  <button
                    onClick={() => setFrageIdx(frageIdx + 1)}
                    disabled={userAnswers.length === 0}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#338edc', color: '#fff' }}
                  >
                    {t('qmTest.weiter') || 'Weiter'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={submitTest}
                    disabled={submitting || beantwortet < versuch.fragen.length}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#10B981', color: '#fff' }}
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {t('qmTest.absenden') || 'Absenden'}
                  </button>
                )}
              </div>
              {error && (
                <div className="rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>
                  {error}
                </div>
              )}
            </div>
          );
        })()}

        {/* === RESULT === */}
        {phase === 'result' && result && (
          <div className="space-y-4">
            <div className="rounded-2xl p-6 text-center" style={{ background: result.bestanden ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${result.bestanden ? '#A7F3D0' : '#FECACA'}` }}>
              <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ background: result.bestanden ? '#10B981' : '#EF4444' }}>
                {result.bestanden
                  ? <Trophy className="w-8 h-8" style={{ color: '#fff' }} strokeWidth={1.5} />
                  : <XCircle className="w-8 h-8" style={{ color: '#fff' }} strokeWidth={1.5} />}
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                {result.bestanden ? (t('qmTest.bestanden') || 'Bestanden!') : (t('qmTest.nichtBestanden') || 'Nicht bestanden')}
              </h2>
              <p className="text-3xl font-bold my-3" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                {result.score_pct}%
              </p>
              <p className="text-xs" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                {t('qmTest.bestehensgrenze') || 'Bestehensgrenze'}: {result.bestehensgrenze_pct}%
              </p>
              {result.richtig != null && (
                <p className="text-xs mt-2" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                  {result.richtig} / {result.gesamt} {t('qmTest.fragenRichtig') || 'Fragen richtig'}
                </p>
              )}
            </div>

            {!result.bestanden && (
              <div className="rounded-2xl p-4" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <p className="text-sm font-medium mb-1" style={{ color: '#92400E' }}>
                  {t('qmTest.cooldownTitle') || 'Erneut versuchen'}
                </p>
                <p className="text-xs" style={{ color: '#92400E' }}>
                  {t('qmTest.cooldownHint') || 'Du kannst nach Ablauf der Cooldown-Zeit erneut antreten. Die Tour kannst du jederzeit wiederholen.'}
                </p>
              </div>
            )}

            <button
              onClick={() => navigate('/portal/schulungen')}
              className="w-full py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2"
              style={{ background: '#338edc', color: '#fff' }}
            >
              {t('qmTest.zurUebersicht') || 'Zur Übersicht'}
            </button>
            {!result.bestanden && !result.isFromHistory && (
              <button
                onClick={() => { setPhase('intro'); setResult(null); setError(null); }}
                className="w-full py-3 rounded-2xl text-sm font-medium border"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)', background: 'var(--bg-card, #FFFFFF)' }}
              >
                {t('qmTest.erneut') || 'Erneut'}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
